import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppShell } from "./components/layout/AppShell";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { config } from "./lib/config";
import { AuditPage } from "./pages/audit/AuditPage";
import { HomePage } from "./pages/HomePage";
import { IngestPage } from "./pages/ingest/IngestPage";
import { EsploraPage } from "./pages/kb/EsploraPage";
import { KnowledgeBasePage } from "./pages/kb/KnowledgeBasePage";
import { StrategyPage } from "./pages/strategy/StrategyPage";
import { SuppliersPage } from "./pages/suppliers/SuppliersPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Layout route: gates the whole app behind Auth0. Logged-out users see the
 * login screen; authenticated users get the AppShell chrome wrapping the
 * active stage (rendered via <Outlet/>).
 */
function ProtectedLayout(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--c-ink-3)",
        }}
      >
        Caricamento…
      </div>
    );
  }
  if (!isAuthenticated) return <HomePage />;
  return <AppShell />;
}

/** ErrorBoundary keyed to the route: navigating (incl. browser Back) clears a
 *  shown error instead of freezing the app on the fallback screen. */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }): JSX.Element {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<IngestPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/audit/:projectId" element={<AuditPage />} />
        <Route path="/strategy" element={<StrategyPage />} />
        <Route path="/kb" element={<KnowledgeBasePage />} />
        <Route path="/kb/explore" element={<EsploraPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App(): JSX.Element {
  const authConfig = useMemo(
    () => ({
      domain: config.auth0Domain,
      clientId: config.auth0ClientId,
      // Persist tokens in localStorage so the KB-audience consent (granted via
      // the popup) survives reloads for the token's lifetime — otherwise
      // localhost re-prompts on every reload (Auth0 can't skip consent there and
      // the silent flow can't show it). One click then lasts ~a day. Indefinite
      // renewal would need refresh tokens (Auth0 "Allow Offline Access" + token
      // rotation) — a prod-phase follow-up; prod on a real domain skips consent
      // for first-party apps entirely.
      cacheLocation: "localstorage" as const,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: config.auth0Audience,
        // Org required because the SPA is configured for Business Users in
        // Auth0. Defaults to the "personal" dev org; production will swap
        // this to the user's resolved org_id via an email-lookup flow.
        organization: config.auth0Organization,
      },
    }),
    [],
  );

  return (
    <Auth0Provider {...authConfig}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* Crash-safety net: real tender data is where unexpected shapes
              appear; one render throw must not blank the whole SPA. */}
          <RoutedErrorBoundary>
            <AppRoutes />
          </RoutedErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </Auth0Provider>
  );
}
